import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { createApiUrlWithPrefix, createApiUrl } from '../config/api';
import { Check, X, Eye, Trash2, RefreshCw, FileText, Camera } from 'lucide-react';
import UserCreationModal from './UserCreationModal';
import Modal, { ModalFooterBar } from './ui/Modal';
import { useUserCounts } from '../contexts/UserCountsContext';

function Subscribers() {
  const { t } = useTranslation();
  const { refreshUserCounts } = useUserCounts();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('0'); // Default to '0' = Pending only
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== '') {
        params.append('status', statusFilter);
      }
      params.append('page', page);
      params.append('pageSize', pageSize);

      const queryString = params.toString();
      const url = createApiUrlWithPrefix(`subscribers${queryString ? `?${queryString}` : ''}`);
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.items) {
        setSubscribers(response.data.items);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.totalItems);
        // Update pending count based on API totalItems (which reflects filtered pending if status=0)
        setPendingCount(response.data.totalItems || 0);
      } else {
        // Fallback for backward compatibility or error
        const list = Array.isArray(response.data) ? response.data : [];
        setSubscribers(list);
        // Compute pending count manually when not provided
        const pending = list.filter(s => s.status === 0).length;
        setPendingCount(pending);
      }
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
      toast.error(error.response?.data?.message || t('subscribers.toastLoadError'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, pageSize, t]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const handleApprove = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setShowApproveModal(true);
  };

  const handleUserCreated = async (newUser) => {
    // After user is created via the form, mark the subscriber as converted
    if (selectedSubscriber && newUser && newUser.id) {
      try {
        // Mark subscriber as converted with the newly created user ID
        await axios.put(
          createApiUrlWithPrefix(`subscribers/${selectedSubscriber.id}/convert`),
          newUser.id, // Send userId as body
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );

        toast.success(t('subscribers.toastApproveSuccess'));
        setShowApproveModal(false);
        setSelectedSubscriber(null);

        // Force refresh the subscribers list to remove the approved subscriber
        // Ensure we're still filtering by pending status (0)
        setStatusFilter('0');
        await fetchSubscribers();
        refreshUserCounts(); // Refresh the subscriber count in navigation
      } catch (error) {
        console.error('Failed to mark subscriber as converted:', error);
        // Even if marking as converted fails, the user was created successfully
        toast.success(t('subscribers.toastConvertSuccess'));
        setShowApproveModal(false);
        setSelectedSubscriber(null);

        // Force refresh even on error
        setStatusFilter('0');
        await fetchSubscribers();
        refreshUserCounts();
      }
    }
  };

  const handleReject = async (subscriberId) => {
    if (!window.confirm(t('subscribers.confirmReject'))) {
      return;
    }

    try {
      await axios.post(
        createApiUrlWithPrefix(`subscribers/${subscriberId}/reject`),
        null,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      toast.success(t('subscribers.toastRejectSuccess'));
      // Ensure filter is set to pending and refresh
      setStatusFilter('0');
      await fetchSubscribers();
      refreshUserCounts(); // Refresh the subscriber count in navigation
    } catch (error) {
      console.error('Failed to reject subscriber:', error);
      toast.error(error.response?.data?.message || t('subscribers.toastRejectError'));
    }
  };

  const handleDelete = async (subscriberId) => {
    if (!window.confirm(t('subscribers.confirmDelete'))) {
      return;
    }

    try {
      await axios.delete(createApiUrlWithPrefix(`subscribers/${subscriberId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success(t('subscribers.toastDeleteSuccess'));
      // Ensure filter is set to pending and refresh
      setStatusFilter('0');
      await fetchSubscribers();
      refreshUserCounts(); // Refresh the subscriber count in navigation
    } catch (error) {
      console.error('Failed to delete subscriber:', error);
      toast.error(error.response?.data?.message || t('subscribers.toastDeleteError'));
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: t('subscribers.statusPending'), color: 'bg-yellow-100 text-yellow-800' },
      1: { label: t('subscribers.statusApproved'), color: 'bg-green-100 text-green-800' },
      2: { label: t('subscribers.statusRejected'), color: 'bg-red-100 text-red-800' },
      3: { label: t('subscribers.statusConverted'), color: 'bg-blue-100 text-blue-800' }
    };

    const statusInfo = statusMap[status] || statusMap[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('subscribers.notAvailable');
    return new Date(dateString).toLocaleDateString();
  };

  const memoizedInitialData = React.useMemo(() => {
    if (!selectedSubscriber) return null;
    return {
      id: selectedSubscriber.id,
      firstName: selectedSubscriber.firstName,
      lastName: selectedSubscriber.lastName,
      email: selectedSubscriber.email,
      phoneNumber: selectedSubscriber.phoneNumber,
      birthDate: selectedSubscriber.birthDate ? selectedSubscriber.birthDate.split('T')[0] : '',
      address: selectedSubscriber.address,
      bloodType: selectedSubscriber.bloodType,
      photoPath: selectedSubscriber.photoPath,
      pjDocumentPath: selectedSubscriber.pjDocumentPath,
    };
  }, [selectedSubscriber]);

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">
          {t('subscribers.title')}{pendingCount > 0 && (
            <span className="ml-2 px-2 py-1 bg-indigo-100 text-indigo-800 text-sm rounded">
              {pendingCount} {t('subscribers.pending')}
            </span>
          )}
        </h2>
        <div className="flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="0">{t('subscribers.statusPending')}</option>
            <option value="">{t('subscribers.statusFilterAll')}</option>
            <option value="1">{t('subscribers.statusApproved')}</option>
            <option value="2">{t('subscribers.statusRejected')}</option>
            <option value="3">{t('subscribers.statusConverted')}</option>
          </select>
          <button
            onClick={fetchSubscribers}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t('subscribers.refresh')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('subscribers.loading')}</p>
        </div>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg">
          <p className="text-gray-600">{t('subscribers.empty')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden flex flex-col h-full max-h-[calc(100vh-12rem)]">
          <div className="overflow-x-auto overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscribers.tableName')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscribers.tableEmail')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscribers.tablePhone')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscribers.tableBirthDate')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscribers.tableStatus')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscribers.tableCreatedAt')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscribers.tableActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subscribers.map((subscriber) => (
                  <tr key={subscriber.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {subscriber.firstName} {subscriber.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{subscriber.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{subscriber.phoneNumber || t('subscribers.notAvailable')}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(subscriber.birthDate)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(subscriber.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(subscriber.createdAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setSelectedSubscriber(subscriber);
                            setShowDetailsModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title={t('subscribers.viewDetails')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {subscriber.status === 0 && (
                          <>
                            <button
                              onClick={() => handleApprove(subscriber)}
                              className="text-green-600 hover:text-green-900"
                              title={t('subscribers.approve')}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleReject(subscriber.id)}
                              className="text-red-600 hover:text-red-900"
                              title={t('subscribers.reject')}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(subscriber.id)}
                          className="text-red-600 hover:text-red-900"
                          title={t('subscribers.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('subscribers.previous')}
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('subscribers.next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {t('subscribers.showing')} <span className="font-medium">{(page - 1) * pageSize + 1}</span> {t('subscribers.to')} <span className="font-medium">{Math.min(page * pageSize, totalItems)}</span> {t('subscribers.of')} <span className="font-medium">{totalItems}</span> {t('subscribers.results')}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label={t('subscribers.paginationLabel')}>
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">{t('subscribers.previous')}</span>
                    {t('subscribers.previous')}
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === i + 1
                          ? 'z-10 bg-green-50 border-green-500 text-green-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">{t('subscribers.next')}</span>
                    {t('subscribers.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      <Modal
        isOpen={showDetailsModal && !!selectedSubscriber}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedSubscriber(null);
        }}
        title={t('subscribers.detailsModalTitle')}
        subtitle={selectedSubscriber ? `${selectedSubscriber.firstName} ${selectedSubscriber.lastName}` : ''}
        icon={Eye}
        variant="indigo"
        size="lg"
        footer={
          <ModalFooterBar>
            <button
              type="button"
              onClick={() => {
                setShowDetailsModal(false);
                setSelectedSubscriber(null);
              }}
              className="px-5 py-2 text-sm font-medium bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              {t('subscribers.close')}
            </button>
          </ModalFooterBar>
        }
      >
        {selectedSubscriber && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelFirstName')}</label>
                <p className="text-gray-900">{selectedSubscriber.firstName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelLastName')}</label>
                <p className="text-gray-900">{selectedSubscriber.lastName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelEmail')}</label>
                <p className="text-gray-900">{selectedSubscriber.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelPhoneNumber')}</label>
                <p className="text-gray-900">{selectedSubscriber.phoneNumber || t('subscribers.notAvailable')}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelBirthDate')}</label>
                <p className="text-gray-900">{formatDate(selectedSubscriber.birthDate)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelBloodType')}</label>
                <p className="text-gray-900">{selectedSubscriber.bloodType || t('subscribers.notAvailable')}</p>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelAddress')}</label>
                <p className="text-gray-900">{selectedSubscriber.address || t('subscribers.notAvailable')}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelStatus')}</label>
                <div className="mt-1">{getStatusBadge(selectedSubscriber.status)}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelCreatedAt')}</label>
                <p className="text-gray-900">{formatDate(selectedSubscriber.createdAt)}</p>
              </div>
              {selectedSubscriber.notes && (
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700">{t('subscribers.labelNotes')}</label>
                  <p className="text-gray-900">{selectedSubscriber.notes}</p>
                </div>
              )}

              {/* Uploaded Documents */}
              <div className="col-span-2 mt-4">
                <label className="text-sm font-medium text-gray-700 block mb-3">{t('subscribers.uploadedDocuments')}</label>
                <div className="flex gap-8">
                  {/* Photo */}
                  <div className="text-center">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('subscribers.profilePhoto')}</p>
                    {selectedSubscriber.photoPath ? (
                      <a href={createApiUrl(selectedSubscriber.photoPath)} target="_blank" rel="noopener noreferrer" className="block relative group">
                        <img
                          src={createApiUrl(selectedSubscriber.photoPath)}
                          alt={t('subscribers.profilePhoto')}
                          className="w-32 h-32 object-cover rounded-xl border-2 border-gray-200 shadow-sm group-hover:border-primary-500 transition-colors"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-xl transition-all">
                          <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100" />
                        </div>
                      </a>
                    ) : (
                      <div className="w-32 h-32 bg-gray-50 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-gray-200 text-gray-400">
                        <Camera className="w-8 h-8 mb-2 opacity-50" />
                        <span className="text-xs">{t('subscribers.noPhoto')}</span>
                      </div>
                    )}
                  </div>

                  {/* PJ Document */}
                  <div className="text-center">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('subscribers.inscriptionDoc')}</p>
                    {selectedSubscriber.pjDocumentPath ? (
                      <a
                        href={createApiUrl(selectedSubscriber.pjDocumentPath)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-32 h-32 bg-blue-50 rounded-xl flex flex-col items-center justify-center border-2 border-blue-100 text-blue-600 hover:bg-blue-100 transition-colors group"
                      >
                        <FileText className="w-10 h-10 mb-2 group-hover:scale-110 transition-transform" />
                        <span className="text-xs font-medium">{t('subscribers.viewDocument')}</span>
                      </a>
                    ) : (
                      <div className="w-32 h-32 bg-gray-50 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-gray-200 text-gray-400">
                        <FileText className="w-8 h-8 mb-2 opacity-50" />
                        <span className="text-xs">{t('subscribers.noDocument')}</span>
                      </div>
                    )}
                  </div>

                  {/* Birth Certificate */}
                  <div className="text-center">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('subscribers.birthCertificate')}</p>
                    {selectedSubscriber.birthCertificatePath ? (
                      <a
                        href={createApiUrl(selectedSubscriber.birthCertificatePath)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-32 h-32 bg-green-50 rounded-xl flex flex-col items-center justify-center border-2 border-green-100 text-green-600 hover:bg-green-100 transition-colors group"
                      >
                        <FileText className="w-10 h-10 mb-2 group-hover:scale-110 transition-transform" />
                        <span className="text-xs font-medium">{t('subscribers.viewDocument')}</span>
                      </a>
                    ) : (
                      <div className="w-32 h-32 bg-gray-50 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-gray-200 text-gray-400">
                        <FileText className="w-8 h-8 mb-2 opacity-50" />
                        <span className="text-xs">{t('subscribers.noDocument')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Approve Modal - Use same form as Riders page */}
      {showApproveModal && selectedSubscriber && (
        <UserCreationModal
          isOpen={showApproveModal}
          onClose={() => {
            setShowApproveModal(false);
            setSelectedSubscriber(null);
          }}
          role={null}
          mode="approve"
          onSubmit={async (approveData) => {
            // Use the approve endpoint
            try {
              const response = await axios.post(
                createApiUrlWithPrefix(`subscribers/${selectedSubscriber.id}/approve`),
                approveData,
                {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                  }
                }
              );

              const newUser = response.data.User || response.data.user;

              toast.success(t('subscribers.toastApproveSuccess'));
              setShowApproveModal(false);
              setSelectedSubscriber(null);
              setStatusFilter('0');
              await fetchSubscribers();
              refreshUserCounts();
            } catch (error) {
              console.error('Failed to approve subscriber:', error);
              toast.error(error.response?.data?.message || t('subscribers.toastApproveError'));
              throw error;
            }
          }}
          onUserCreated={handleUserCreated}
          initialData={memoizedInitialData}
        />
      )}
    </div>
  );
}

export default Subscribers;
